/**
 * LowGlucoseEventsPanel - Low Glucose Events Display for Dashboard (Part 7)
 *
 * Shows low glucose events (glucose below the low threshold — the prop/species
 * targetMin, else GLUCOSE_THRESHOLDS.LOW, 4.4 mmol/L) for risk assessment:
 * - Events sorted newest first
 * - Grouped by date
 * - Shows time and glucose value for each event
 * - "No events" message when empty
 */

import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import WarningIcon from '@mui/icons-material/Warning'
import { Alert, Box, Chip, Paper, Skeleton, Typography } from '@mui/material'
import { format, parseISO } from 'date-fns'
import React, { useMemo } from 'react'
import { useStore, useTranslate } from 'react-admin'
import { formatGlucoseForDisplay, normalizeGlucoseReadingForChart, smoothCGM } from '../../common'
import { useDeviceMetrics } from '../../hooks/useDeviceMetrics'
import { GLUCOSE_THRESHOLDS, LowGlucoseEvent, TIR_ZONE_COLORS } from '../../types/dashboard'
import { SHARED_METRICS_PER_PAGE } from '../../utils/paginationUtils'

export interface LowGlucoseEventsPanelProps {
  deviceId?: string | number
  petId?: string | number
  startDate?: Date
  endDate?: Date
  maxHeight?: number
  threshold?: number
  targetMin?: number
}

interface GroupedEvents {
  date: Date
  dateLabel: string
  events: LowGlucoseEvent[]
}

export const LowGlucoseEventsPanel: React.FC<LowGlucoseEventsPanelProps> = React.memo(
  ({
    deviceId: propDeviceId,
    petId: propPetId,
    startDate,
    endDate,
    maxHeight = 400,
    threshold: propThreshold,
    targetMin,
  }) => {
    const threshold = propThreshold ?? targetMin ?? GLUCOSE_THRESHOLDS.LOW
    const translate = useTranslate()
    const [storeDeviceId] = useStore<any>('selectedDeviceId')
    const [storePetId] = useStore<any>('selectedPetId')
    const [glucoseUnit] = useStore('glucoseUnit', 'mmol/L')
    const deviceId = propDeviceId ?? storeDeviceId
    const petId = propPetId ?? storePetId

    // Format glucose value for display.
    //
    // Uses the SHARED formatter rather than a hand-rolled toFixed (L-036). The
    // shared one carries the out-of-range mask, so a sub-floor episode nadir
    // reads "<40" instead of a confident "40" a vet cannot distinguish from a
    // real reading — which matters most in THIS panel, whose entire job is
    // reporting low-glucose episodes. `withUnit: false` because the unit is
    // rendered as a separate caption beside the number.
    const formatGlucose = (mmolValue: number) =>
      formatGlucoseForDisplay(mmolValue, glucoseUnit as string, { withUnit: false })

    // Use the unified metrics hook that handles virtual devices
    const {
      data: metricsData = [],
      isLoading,
      error,
    } = useDeviceMetrics({
      deviceId,
      petId,
      startDate,
      endDate,
      sort: { field: 'timestamp', order: 'DESC' }, // Newest first for low glucose events
      pagination: { page: 1, perPage: SHARED_METRICS_PER_PAGE },
    })

    // Filter and group low glucose events
    const groupedEvents = useMemo((): GroupedEvents[] => {
      if (!metricsData || metricsData.length === 0) return []

      // Detect low events from the SAME pipeline the chart/PDF render:
      // normalize → smoothCGM. smoothCGM is a moving average and is order
      // dependent, so sort ascending by timestamp first. Without smoothing, a
      // single out-of-range reading the chart smooths away would still surface
      // here as a low event (e.g. a one-off 63 mg/dL between normal readings).
      const normalizedMetrics = [...metricsData]
        .sort((a: any, b: any) => (a.timestamp || 0) - (b.timestamp || 0))
        .map((metric: any) => {
          const normalized = normalizeGlucoseReadingForChart(metric)
          return normalized ? { ...metric, glucose: normalized.glucose } : null
        })
        .filter((m): m is NonNullable<typeof m> => m !== null)

      const smoothedMetrics = smoothCGM(normalizedMetrics)

      const lowGlucoseMetrics = smoothedMetrics.filter(
        (metric: any) => metric.glucose && metric.glucose < threshold && metric.timestamp,
      )

      if (lowGlucoseMetrics.length === 0) return []

      // Collapse consecutive sub-threshold readings into EPISODES. One 45-minute
      // dip is one clinical event, not 15 — the Go alert engine counts sustained
      // episodes, and a vet reading "15 events" would infer 15 separate dips and
      // may change the insulin dose on it. Chain while the gap is under 30
      // minutes, matching veterinaryPDFExport.detectNotableSegments. Readings
      // are ascending here (smoothCGM requires it).
      const EPISODE_GAP_SECONDS = 30 * 60
      const runs: any[][] = []
      lowGlucoseMetrics.forEach((metric: any) => {
        const current = runs[runs.length - 1]
        const previous = current?.[current.length - 1]
        if (current && previous && metric.timestamp - previous.timestamp < EPISODE_GAP_SECONDS) {
          current.push(metric)
        } else {
          runs.push([metric])
        }
      })

      // One event per episode, newest first. `glucose` is the episode NADIR —
      // the number that drives the severity color and the number a vet acts on
      // (the first reading of a dip is by definition the mildest).
      const episodes: LowGlucoseEvent[] = runs
        .map((run) => {
          const first = run[0]
          const last = run[run.length - 1]
          const nadir = run.reduce((worst, m) => (m.glucose < worst.glucose ? m : worst), first)
          return {
            id: `${first.timestamp}-${run.length}`,
            timestamp: new Date(first.timestamp * 1000),
            endTimestamp: new Date(last.timestamp * 1000),
            glucose: nadir.glucose,
            // Finally populates the field the type declared from the start.
            duration: Math.round((last.timestamp - first.timestamp) / 60),
            readingCount: run.length,
            deviceId: typeof first.device === 'object' ? first.device?.id : first.device || 0,
          }
        })
        .sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime())

      // Group by the date the episode STARTED (an episode straddling midnight
      // belongs to the night it began).
      const groups: Map<string, LowGlucoseEvent[]> = new Map()
      episodes.forEach((event) => {
        const dateKey = format(event.timestamp, 'yyyy-MM-dd')
        if (!groups.has(dateKey)) {
          groups.set(dateKey, [])
        }
        groups.get(dateKey)!.push(event)
      })

      // Convert to array and sort groups by date (newest first)
      return Array.from(groups.entries())
        .map(([dateKey, events]) => ({
          date: parseISO(dateKey),
          dateLabel: format(parseISO(dateKey), 'EEEE, MMMM d, yyyy'),
          events,
        }))
        .sort((a, b) => b.date.getTime() - a.date.getTime())
    }, [metricsData, threshold])

    // Count total events
    const totalEvents = useMemo(() => {
      return groupedEvents.reduce((sum, group) => sum + group.events.length, 0)
    }, [groupedEvents])

    // Determine severity based on glucose value
    const getSeverityColor = (glucose: number): string => {
      if (glucose < GLUCOSE_THRESHOLDS.VERY_LOW) {
        return TIR_ZONE_COLORS.veryLow // Critical
      }
      return TIR_ZONE_COLORS.low // Low but not critical
    }

    const getSeverityLabel = (glucose: number): string => {
      if (glucose < GLUCOSE_THRESHOLDS.VERY_LOW) {
        return translate('pos.glucose.very_low')
      }
      return translate('pos.glucose.low')
    }

    if (isLoading) {
      return (
        <Box sx={{ p: 2 }}>
          <Skeleton variant="rectangular" height={200} />
        </Box>
      )
    }

    if (error) {
      return (
        <Box sx={{ p: 2 }}>
          <Typography color="error">{translate('pos.dashboard.error_loading_data')}</Typography>
        </Box>
      )
    }

    return (
      <Box>
        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <WarningIcon color={totalEvents > 0 ? 'warning' : 'disabled'} />
            {translate('pos.glucose.low_glucose_events')}
          </Typography>
          <Chip
            label={`${totalEvents} ${translate('pos.glucose.events')}`}
            size="small"
            color={totalEvents > 0 ? 'warning' : 'default'}
            variant={totalEvents > 0 ? 'filled' : 'outlined'}
          />
        </Box>

        {/* Threshold info */}
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
          {translate('pos.glucose.showing_events_below', {
            threshold: formatGlucose(threshold),
            unit: glucoseUnit,
          })}
        </Typography>

        {/* Events List */}
        <Box
          sx={{
            maxHeight,
            overflowY: 'auto',
          }}
        >
          {totalEvents === 0 ? (
            <Alert icon={<CheckCircleIcon fontSize="inherit" />} severity="success" sx={{ mt: 2 }}>
              <Typography variant="body2">{translate('pos.glucose.no_low_events')}</Typography>
              <Typography variant="caption" color="text.secondary">
                {translate('pos.glucose.no_low_events_description', {
                  threshold: formatGlucose(threshold),
                  unit: glucoseUnit,
                })}
              </Typography>
            </Alert>
          ) : (
            groupedEvents.map((group) => (
              <Box key={group.dateLabel} sx={{ mb: 3 }}>
                {/* Date Header */}
                <Typography
                  variant="subtitle2"
                  sx={{
                    fontWeight: 600,
                    color: 'text.secondary',
                    mb: 1,
                    pb: 0.5,
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                  }}
                >
                  {group.dateLabel}
                  <Chip
                    label={group.events.length}
                    size="small"
                    sx={{ ml: 1, height: 20, fontSize: '0.7rem' }}
                  />
                </Typography>

                {/* Events for this date */}
                {group.events.map((event) => (
                  <Paper
                    key={event.id}
                    data-testid="low-glucose-episode"
                    sx={{
                      p: 1.5,
                      mb: 1,
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      borderLeft: '4px solid',
                      borderColor: getSeverityColor(event.glucose),
                      '&:hover': {
                        bgcolor: 'grey.50',
                      },
                    }}
                    elevation={0}
                    variant="outlined"
                  >
                    {/* Time span of the episode */}
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {event.endTimestamp && event.duration
                          ? `${format(event.timestamp, 'HH:mm')}–${format(event.endTimestamp, 'HH:mm')}`
                          : format(event.timestamp, 'HH:mm')}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {event.duration
                          ? translate('pos.glucose.episode_duration', {
                              minutes: event.duration,
                            })
                          : format(event.timestamp, 'h:mm a')}
                      </Typography>
                      {/* Reading density: collapsing N rows into one episode
                          must not lose it — 30 min of 11 readings and 30 min
                          of 2 readings are clinically different. */}
                      {event.readingCount != null && event.readingCount > 1 && (
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          data-testid="episode-readings"
                          sx={{ display: 'block' }}
                        >
                          {translate('pos.glucose.episode_readings', {
                            count: event.readingCount,
                          })}
                        </Typography>
                      )}
                    </Box>

                    {/* Episode nadir */}
                    <Box sx={{ textAlign: 'right' }}>
                      <Typography
                        variant="h6"
                        data-testid="episode-nadir"
                        sx={{
                          fontWeight: 700,
                          color: getSeverityColor(event.glucose),
                          lineHeight: 1,
                        }}
                      >
                        {formatGlucose(event.glucose)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {glucoseUnit}
                      </Typography>
                    </Box>

                    {/* Severity Badge */}
                    <Chip
                      label={getSeverityLabel(event.glucose)}
                      size="small"
                      sx={{
                        bgcolor: getSeverityColor(event.glucose),
                        color: 'white',
                        fontWeight: 500,
                        fontSize: '0.7rem',
                      }}
                    />
                  </Paper>
                ))}
              </Box>
            ))
          )}
        </Box>
      </Box>
    )
  },
)

export default LowGlucoseEventsPanel
